import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from '../../../services/user.service';
import {ActionSource, Page, UserActionType} from '../../../services/event-tracker/event-tracker.service';
import {Report, ReportSectionEnum} from '../../../../models/measure/report';
import {MeasureModule} from '../../../../models/measure/measure-module';
import {ObjectUtils} from '../../../utils/object.utils';
import { CommonConstant } from '../../../app.constants';

@Component({
  selector: 'measure-module-dropdown',
  templateUrl: './measure-module-dropdown.component.html',
  styleUrls: ['./measure-module-dropdown.component.scss']
})
export class MeasureModuleDropdownComponent implements OnInit, OnChanges {
  @Input() selectedReport: Report;
  @Input() isEditable: boolean;
  @Input() moduleList: MeasureModule[];
  @Output() afterActivateModule: EventEmitter<any> = new EventEmitter();
  @Output() invalidModification: EventEmitter<any> = new EventEmitter();
  public availableModuleList: MeasureModule[] = [];
  private selectedModuleIds: number[] = [];

  public selectedGroupId = CommonConstant.ALL_MODULES_ID;
  readonly moduleGroups = [
    {
      id: 1,
      name: this.translate.instant('module_groups.selected'),
      modules: []
    },
    {
      id: 2,
      name: this.translate.instant('module_groups.all'),
      modules: []
    },
    {
      id: 3,
      name: this.translate.instant('module_groups.performance'),
      modules: ['Performance Timeline', 'Performance KPIs', 'Market Drill-down', 'Mention Timeline', 'Best Performing Influencers', 'Interactions Report', 'Post Benchmark']
    },
    {
      id: 4,
      name: this.translate.instant('module_groups.exposure'),
      modules: ['Mention Timeline', 'Exposure KPIs', 'Platform Drill-down', 'Best Performance Exposures', 'Interactions Report', 'Post Benchmark']
    },
    {
      id: 5,
      name: this.translate.instant('module_groups.segmentation'),
      modules: ['Exposure KPIs', 'Influencers Overview', 'Topics Drill-down', 'Industries Drill-down']
    },
    {
      id: 6,
      name: this.translate.instant('module_groups.insight'),
      modules: ['Channel Opportunities', 'Average Benchmark Performance', 'Return on Investment (ROI)']
    }
  ];

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isEditable && changes.isEditable.previousValue && changes.isEditable.currentValue
      && !ObjectUtils.isEquals(changes.isEditable.currentValue, changes.isEditable.previousValue)) {
      if (changes.isEditable.currentValue) {
        this.initializeModuleList(this.selectedReport);
      } else {
        this.availableModuleList = this.availableModuleList.filter(m => m.isActive);
      }
    }
    if (changes.selectedReport && changes.selectedReport.currentValue && changes.selectedReport.previousValue
        && !ObjectUtils.isEquals(changes.selectedReport.currentValue, changes.selectedReport.previousValue)) {
      const selectedGroup = this.moduleGroups.find(mg => mg.id === this.selectedGroupId);
      const moduleIds = changes.selectedReport.currentValue.sectionList.map(s => s.id);
      if (this.isEditable) {
        this.updateModuleList(moduleIds, selectedGroup);
      } else {
        this.selectedModuleIds = moduleIds;
        this.availableModuleList = moduleIds.map((sm, index) => {
          const module = this.moduleList.find(m => m.id === sm);
          return Object.assign({}, module, {
            isActive: true,
            position: index
          });
        });
      }
    }
  }

  constructor(private translate: TranslateService,
              private userService: UserService) { }

  ngOnInit() {
    this.initializeModuleList(this.selectedReport);
  }

  private initializeModuleList(selectedReport) {
    this.selectedModuleIds = selectedReport.sectionList.map(s => s.id);
    this.availableModuleList = this.selectedModuleIds.map((sm, index) => {
      const module = this.moduleList.find(m => m.id === sm);
      return Object.assign({}, module, {
        isActive: true,
        position: index
      });
    });
  }

  get isFreeUser() {
    return this.userService.isFreeUser();
  }

  onSelectGroup(evt) {
    if (evt) {
      const selectedGroup = this.moduleGroups.find(mg => mg.id === evt.id);
      this.updateModuleList(this.selectedModuleIds, selectedGroup);
    }
  }

  measureContextData() {
    let data = {
      action: UserActionType.ACTION,
      item: Page.REPORT,
      params: {
        action: this.selectedReport ? UserActionType.UPDATE : UserActionType.CREATE,
        source: ActionSource.PAGE
      },
    };

    if (this.selectedReport) {
      data = Object.assign({}, data, {
        report: {
          id: this.selectedReport.id,
          name: this.selectedReport.name
        }
      });
    }
    return data;
  }

  onActivateModule(moduleId) {
    const selectedIndex = this.selectedModuleIds.indexOf(moduleId);
    const module = this.availableModuleList.find(m => m.id === moduleId);
    if (!module) {
      return;
    }
    if (selectedIndex >= 0) {
      this.selectedModuleIds.splice(selectedIndex, 1);
      module.isActive = false;
    } else {
      module.isActive = true;
      const insertIndex = this.findActiveModulePosition(moduleId);
      this.selectedModuleIds.splice(insertIndex, 0, moduleId);
    }
    const newSelectedModuleList = this.selectedModuleIds.map(sm => this.moduleList.find(m => m.id === sm));
    this.afterActivateModule.emit(newSelectedModuleList);
  }

  private findActiveModulePosition(module) {
    const activePos = this.availableModuleList.findIndex(am => am.id === module);
    const firstActiveModuleAfter = this.availableModuleList.find((m, index) => m.isActive && index > activePos);
    if (!firstActiveModuleAfter) {
      const firstActiveModuleBefore = this.availableModuleList.find((m, index) => m.isActive && index < activePos);
      return !firstActiveModuleBefore ? null : this.selectedModuleIds.indexOf(firstActiveModuleBefore.id) + 1;
    } else {
      return this.selectedModuleIds.indexOf(firstActiveModuleAfter.id);
    }
  }

  private updateModuleList(selectedModuleIds, selectedGroup) {
    if (selectedGroup.id === 1) {
      this.availableModuleList = selectedModuleIds.map(sm => this.moduleList.find(m => m.id === sm));
    } else if (selectedGroup.id === CommonConstant.ALL_MODULES_ID) {
      this.availableModuleList = this.moduleList.map(sm => sm);
    } else {
      this.availableModuleList = this.moduleList.filter(m => selectedGroup.modules.indexOf(m.name) >= 0);
    }
    this.updateSelectedModule();
  }

  private updateSelectedModule() {
    this.availableModuleList.forEach((sm) => {
      if (this.selectedModuleIds.indexOf(sm.id) >= 0) {
        sm.isActive = true;
      } else {
        sm.isActive = false;
      }
    });
  }

  public afterDropModule(dropEvent) {
    // not allow to update default report
    if (this.selectedReport.is_default_report) {
      this.invalidModification.emit(true);
      return;
    }
    // swap selected module index
    const droppedModule = this.availableModuleList[dropEvent.draggedModuleIndex];
    this.availableModuleList[dropEvent.draggedModuleIndex] = this.availableModuleList[dropEvent.droppedModuleIndex];
    this.availableModuleList[dropEvent.droppedModuleIndex] = droppedModule;
    const activeModules = this.availableModuleList.filter(m => m.isActive);
    const activeModulesIds = activeModules.map(am => am.id);
    const nextSelectedModulesId = this.selectedModuleIds.map(sm => {
      if (activeModulesIds.indexOf(sm) >= 0) {
        return -1;
      } else {
        return sm;
      }
    });
    activeModulesIds.forEach(m => {
      const mIndex = nextSelectedModulesId.indexOf(-1);
      nextSelectedModulesId.splice(mIndex, 1, m);
    });

    this.selectedModuleIds = nextSelectedModulesId;
    console.log(this.selectedModuleIds);
    const nextSelectedModuleList = nextSelectedModulesId.map(ms => this.moduleList.find(m => m.id === ms));
    this.afterActivateModule.emit(nextSelectedModuleList);
  }

  get selectedModuleName() {
    const selectedModule = this.moduleGroups.find(mg => mg.id === this.selectedGroupId);
    if (!selectedModule) {
      return '';
    }
    const moduleIndex = selectedModule.name.indexOf('Module');
    return selectedModule.name.substring(0, moduleIndex);
  }

  get shortModule() {
    return this.selectedGroupId === CommonConstant.ALL_MODULES_ID;
  }

  get middleShortModule() {
    return this.selectedGroupId === 6;
  }

  get mediumModule() {
    return this.selectedGroupId === 1 || this.selectedGroupId === 4;
  }

  get shouldDisableFreemiumFeature() {
    return this.isFreeUser && !this.userService.isAdmin;
  }

  public initializeSelectedModuleForNewReport() {
    this.selectedModuleIds = [];
  }

  closeTooltip(t) {
    if (t) {
      t.close();
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/main/measure/measure-module-dropdown/measure-module-dropdown.component.ts